import type { GridColDef, GridColumnResizeParams, GridValidRowModel } from '@mui/x-data-grid';
import { useEffect, useMemo, useRef, useState } from 'react';
import { TableColumnsState, useTableColumnsPersistence } from './use-table-columns-persistence';
import { GridApiPro } from '@mui/x-data-grid-pro';
import { isEmptyObject, isEmptyString, isNullOrUndefined } from '@vy/utils';

interface UseTableColumnsManagerParams<Item extends GridValidRowModel> {
  apiRef?: React.MutableRefObject<GridApiPro>;
  columns: GridColDef<Item>[];
  tableName: string;
  apiConfig?: { visibility?: Record<string, boolean> };
}

interface UseTableColumnsManagerResult<Item extends GridValidRowModel> {
  processedColumns: GridColDef<Item>[];
}

interface ColumnConfigModels {
  widths: Record<string, number>;
  visibility: Record<string, boolean>;
  order: string[];
}

function isCustomFieldColumn<Item extends GridValidRowModel>(
  column: GridColDef<Item>,
): column is GridColDef<Item> & { customField?: boolean } {
  return 'customField' in column;
}

function getCustomFieldNames<Item extends GridValidRowModel>(columns: GridColDef<Item>[]): Set<string> {
  const names = new Set<string>();

  for (const column of columns) {
    if (column.field && isCustomFieldColumn(column) && column.customField === true) {
      names.add(column.field);
    }
  }

  return names;
}

function normalizeOrderWithCustomFieldsLast<Item extends GridValidRowModel>(
  columns: GridColDef<Item>[],
  order: string[],
): string[] {
  const customFieldNames = getCustomFieldNames(columns);
  const columnFields = new Set(columns.map((col) => col.field).filter(Boolean));

  const baseOrdered = order.filter((field) => columnFields.has(field) && !customFieldNames.has(field));
  const customOrdered = order.filter((field) => customFieldNames.has(field));
  const other = order.filter((field) => !columnFields.has(field));

  return [...baseOrdered, ...customOrdered, ...other];
}

function applyLoadedConfig(
  loadedConfig: TableColumnsState | undefined,
  models: ColumnConfigModels,
  persistedFields: Set<string>,
): void {
  if (isNullOrUndefined(loadedConfig) || isEmptyObject(loadedConfig.visibility)) {
    return;
  }

  models.order = loadedConfig.order;
  models.widths = loadedConfig.widths;
  models.visibility = loadedConfig.visibility;

  for (const field of Object.keys(loadedConfig.widths)) {
    persistedFields.add(field);
  }
}

function applyApiConfig(
  apiConfig: { visibility?: Record<string, boolean> } | undefined,
  models: ColumnConfigModels,
) {
  if (isNullOrUndefined(apiConfig?.visibility)) {
    return;
  }

  for (const [field, isVisible] of Object.entries(apiConfig.visibility)) {
    if (!(field in models.visibility)) {
      models.visibility[field] = isVisible;
    }
  }
}

function applyDefaultColumnConfig<Item extends GridValidRowModel>(
  columns: GridColDef<Item>[],
  models: ColumnConfigModels,
) {
  for (const column of columns) {
    if (isEmptyString(column.field) || column.field in models.visibility) {
      continue;
    }

    const isCustomField = isCustomFieldColumn(column) && column.customField === true;
    models.visibility[column.field] = !isCustomField;

    if (column.width) {
      models.widths[column.field] = column.width;
    }

    if (!models.order.includes(column.field)) {
      models.order.push(column.field);
    }
  }
}

function useColumnOrderChangeSubscription(
  apiRef: React.MutableRefObject<GridApiPro> | undefined,
  callback: () => void,
) {
  useEffect(() => {
    if (isNullOrUndefined(apiRef?.current)) return;

    return apiRef.current.subscribeEvent('columnOrderChange', callback);
  }, [apiRef]);
}

function useColumnVisibilityModelChangeSubscription(
  apiRef: React.MutableRefObject<GridApiPro> | undefined,
  callback: () => void,
) {
  useEffect(() => {
    if (isNullOrUndefined(apiRef?.current)) return;

    return apiRef.current.subscribeEvent('columnVisibilityModelChange', callback);
  }, [apiRef]);
}

function useColumnWidthChangeSubscription(
  apiRef: React.MutableRefObject<GridApiPro> | undefined,
  callback: (params: GridColumnResizeParams) => void,
) {
  useEffect(() => {
    if (isNullOrUndefined(apiRef?.current)) return;

    return apiRef.current.subscribeEvent('columnWidthChange', callback);
  }, [apiRef]);
}

function pullColumnOrder(apiRef: React.MutableRefObject<GridApiPro> | undefined): string[] {
  if (isNullOrUndefined(apiRef?.current?.state)) return [];

  return apiRef.current.state.columns?.orderedFields ?? [];
}

function pullColumnVisibility(
  apiRef: React.MutableRefObject<GridApiPro> | undefined,
): Record<string, boolean> {
  if (isNullOrUndefined(apiRef?.current?.state)) return {};

  return apiRef.current.state.columns?.columnVisibilityModel ?? {};
}

function pullColumnWidths<Item extends GridValidRowModel>(
  columns: GridColDef<Item>[],
): Record<string, number> {
  const widths: Record<string, number> = {};

  for (const column of columns) {
    if (column.field && column.width) {
      widths[column.field] = column.width;
    }
  }

  return widths;
}

export function useTableColumnsManager<Item extends GridValidRowModel>({
  apiRef,
  columns,
  tableName,
  apiConfig,
}: UseTableColumnsManagerParams<Item>): UseTableColumnsManagerResult<Item> {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => pullColumnWidths(columns));
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(() =>
    pullColumnVisibility(apiRef),
  );
  const [columnsOrderModel, setColumnOrderModel] = useState<string[]>(() => pullColumnOrder(apiRef));

  const isInitialConfigAppliedRef = useRef(false);
  const previousLoadedConfigRef = useRef<TableColumnsState | undefined>(undefined);
  const persistedFieldsRef = useRef<Set<string>>(new Set());
  const userResizedFieldsRef = useRef<Set<string>>(new Set());

  const { loadedConfig } = useTableColumnsPersistence({
    tableName,
    visibility: columnVisibilityModel,
    widths: columnWidths,
    order: columnsOrderModel,
  });

  // apply new config on change
  useEffect(() => {
    if (isNullOrUndefined(apiRef?.current)) {
      return;
    }

    const isLoadedConfigChanged = loadedConfig !== previousLoadedConfigRef.current;
    previousLoadedConfigRef.current = loadedConfig;

    const models: ColumnConfigModels = {
      widths: {},
      visibility: {},
      order: [],
    };

    if (!isInitialConfigAppliedRef.current || isLoadedConfigChanged) {
      applyLoadedConfig(loadedConfig, models, persistedFieldsRef.current);
      isInitialConfigAppliedRef.current = true;
    } else {
      models.visibility = { ...columnVisibilityModel };
      models.widths = { ...columnWidths };
      models.order = [...columnsOrderModel];
    }

    applyApiConfig(apiConfig, models);
    applyDefaultColumnConfig(columns, models);
    models.order = normalizeOrderWithCustomFieldsLast(columns, models.order);

    setColumnWidths(models.widths);
    setColumnVisibilityModel(models.visibility);
    setColumnOrderModel(models.order);

    if (apiRef.current.setColumnVisibilityModel) {
      apiRef.current.setColumnVisibilityModel(models.visibility);
    }

    const checkboxField = '__check__';
    const orderedFields = models.order.filter((field) => field !== checkboxField);
    if (apiRef.current.getColumn(checkboxField)) {
      orderedFields.unshift(checkboxField);
    }

    for (const [index, field] of orderedFields.entries()) {
      apiRef.current.setColumnIndex(field, index);
    }

    for (const [field, width] of Object.entries(models.widths)) {
      apiRef.current.setColumnWidth(field, width);
    }
  }, [loadedConfig, apiRef, columns, apiConfig]);

  useColumnWidthChangeSubscription(apiRef, (params: GridColumnResizeParams) => {
    const field = params.colDef.field;
    const width = params.width;

    persistedFieldsRef.current.add(field);
    userResizedFieldsRef.current.add(field);
    setColumnWidths((prev) => ({ ...prev, [field]: width }));
  });

  useColumnOrderChangeSubscription(apiRef, () => {
    if (isNullOrUndefined(apiRef?.current?.state)) {
      return;
    }

    const newOrderModel: string[] = apiRef.current.state.columns?.orderedFields ?? [];
    setColumnOrderModel(newOrderModel);
  });

  useColumnVisibilityModelChangeSubscription(apiRef, () => {
    if (isNullOrUndefined(apiRef?.current?.state)) {
      return;
    }

    const visibilityModel = pullColumnVisibility(apiRef);
    setColumnVisibilityModel(visibilityModel);
  });

  const processedColumns = useMemo((): GridColDef<Item>[] => {
    return columns.map((col) => {
      if (
        col.field &&
        columnWidths[col.field] &&
        (col.flex ? userResizedFieldsRef.current.has(col.field) : persistedFieldsRef.current.has(col.field))
      ) {
        const { flex: _flex, ...rest } = col;
        return { ...rest, width: columnWidths[col.field] } as GridColDef<Item>;
      }
      return col;
    });
  }, [columns, columnWidths]);

  return { processedColumns };
}
